import { useState } from "react";
import { StepCard } from "./StepCard";
import { VoiceExplanation } from "./VoiceExplanation";
import { CheckCircle, RotateCcw, Languages } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LANGUAGE_NAMES } from "@/lib/languages";

export interface SolutionStep {
  title: string;
  explanation: string;
  work?: string;
}

interface SolutionViewProps {
  concept: string;
  conceptDescription: string;
  steps: SolutionStep[];
  finalAnswer: string;
  summary: string;
  isPaid?: boolean;
  language?: string;
  detectedLanguage?: string;
  originalConcept?: string;
  originalConceptDescription?: string;
  originalSteps?: SolutionStep[];
  originalFinalAnswer?: string;
  originalSummary?: string;
  onPracticeSimilar?: () => void;
  onExplainDeeper?: (
    stepIndex: number,
    step: SolutionStep,
  ) => Promise<string> | string | void;
}

export function SolutionView({
  concept,
  conceptDescription,
  steps,
  finalAnswer,
  summary,
  isPaid = false,
  language = "en",
  detectedLanguage,
  originalConcept,
  originalConceptDescription,
  originalSteps,
  originalFinalAnswer,
  originalSummary,
  onPracticeSimilar,
  onExplainDeeper,
}: SolutionViewProps) {
  const [activeVoiceStep, setActiveVoiceStep] = useState<number | null>(null);
  const [showOriginal, setShowOriginal] = useState(false);

  const hasTranslation = detectedLanguage && detectedLanguage !== language && originalSteps && originalSteps.length > 0;

  const displayConcept = showOriginal && originalConcept ? originalConcept : concept;
  const displayConceptDescription = showOriginal && originalConceptDescription ? originalConceptDescription : conceptDescription;
  const displaySteps = showOriginal && originalSteps ? originalSteps : steps;
  const displayFinalAnswer = showOriginal && originalFinalAnswer ? originalFinalAnswer : finalAnswer;
  const displaySummary = showOriginal && originalSummary ? originalSummary : summary;

  return (
    <div className="space-y-6">
      {/* Language detection badge + toggle */}
      {hasTranslation && (
        <div className="ml-9 md:ml-[50px] flex items-center gap-2 flex-wrap">
          <span className="text-xs bg-neon-blue/10 text-neon-blue px-2.5 py-1 rounded-full font-semibold flex items-center gap-1.5">
            <Languages className="size-3" />
            Detected: {LANGUAGE_NAMES[detectedLanguage] || detectedLanguage}
          </span>
          <button
            onClick={() => setShowOriginal(!showOriginal)}
            className={`text-xs px-2.5 py-1 rounded-full font-semibold flex items-center gap-1.5 transition-colors ${
              showOriginal
                ? "bg-neon-pink/10 text-neon-pink"
                : "bg-muted text-ink-muted hover:bg-muted/80"
            }`}
          >
            <Languages className="size-3" />
            {showOriginal ? `Show ${LANGUAGE_NAMES[language] || language}` : `Show original (${LANGUAGE_NAMES[detectedLanguage] || detectedLanguage})`}
          </button>
        </div>
      )}

      {/* AI avatar + intro */}
      <div className="flex gap-4 items-start">
        <div className="size-10 md:size-12 rounded-full bg-neon-pink shrink-0 flex items-center justify-center font-[family-name:var(--font-display)] font-extrabold text-paper text-lg shadow-sm border-2 border-card">
          M
        </div>
        <div className="flex-1 pt-1">
          <p className="text-xs font-bold uppercase tracking-widest text-neon-blue mb-1">
            {displayConcept}
          </p>
          <p className="text-base md:text-lg font-medium leading-relaxed text-ink">
            {displayConceptDescription}
          </p>
        </div>
      </div>

      {/* Voice Explanation */}
      <div className="ml-9 md:ml-[50px]">
        <VoiceExplanation
          isPaid={isPaid}
          steps={displaySteps}
          finalAnswer={displayFinalAnswer}
          concept={displayConcept}
          language={language}
          onActiveStepChange={setActiveVoiceStep}
        />
      </div>

      {/* Steps */}
      <div className="pl-5 md:pl-[22px] space-y-4 relative step-connector">
        {displaySteps.map((step, index) => (
          <StepCard
            key={index}
            stepNumber={index + 1}
            title={step.title}
            explanation={step.explanation}
            work={step.work}
            isActive={index === 0}
            isVoiceHighlighted={activeVoiceStep === index}
            onExplainDeeper={
              onExplainDeeper ? () => onExplainDeeper(index, step) : undefined
            }
          />
        ))}
      </div>

      {/* Final Answer */}
      <div className="ml-9 md:ml-[50px]">
        <div className="bg-neon-yellow rounded-[2rem] p-6 md:p-8 shadow-[0_16px_40px_-16px_rgba(226,255,61,0.6)] relative overflow-hidden">
          <div className="absolute -right-8 -top-8 size-32 bg-paper/20 rounded-full blur-2xl pointer-events-none" />

          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="size-5 text-ink" />
            <h3 className="font-[family-name:var(--font-display)] font-extrabold text-xl md:text-2xl text-ink">
              Final Answer
            </h3>
          </div>

          <div className="bg-card rounded-xl p-5 text-center shadow-sm">
            <span className="font-[family-name:var(--font-mono)] font-bold text-lg md:text-xl tabular-nums text-ink">
              {displayFinalAnswer}
            </span>
          </div>

          <p className="mt-4 text-sm text-ink/70 leading-relaxed">{displaySummary}</p>

          <div className="mt-5 flex gap-3">
            <Button
              variant="scan"
              size="sm"
              className="flex-1 py-2.5 rounded-xl text-sm"
            >
              <CheckCircle className="size-4" /> Got it!
            </Button>
            {onPracticeSimilar && (
              <Button
                variant="subtle"
                size="sm"
                onClick={onPracticeSimilar}
                className="flex-1 py-2.5 rounded-xl text-sm"
              >
                <RotateCcw className="size-4" /> Practice Similar
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
